import { BundledItem, PurchaseOrderDeliveryOption } from '@/services/api/contracts/catalog/bundles';
import { Product } from '@/services/api/contracts/catalog/types';
import { useCategories } from '@/services/api/hooks/catalog';
import { useCustomerSelectedFinanceModels } from '@/services/api/hooks/companies';
import { useFinanceModels } from '@/services/api/hooks/financemodels';
import { groupProductsByCategories, useProducts } from '@/services/api/hooks/product';
import { PoStatus } from '@/services/purchaseOrders/usePurchaseOrderService';
import { useCallback } from 'react';
import { BundleWithQuantity } from './CreateFromBundlesModal';

export const useBuildPOfromBundlePayload = (mspId: string, customerId: string) => {
  const [categories] = useCategories();
  const [products] = useProducts();
  const [selectedModels] = useCustomerSelectedFinanceModels(customerId, mspId);
  const models = useFinanceModels();

  return useCallback(
    (name: string, bundles: BundleWithQuantity[]) => {
      const bundledItems: BundledItem[] = [];
      const selectedProducts: Product[] = [];
      bundles.forEach(bundle => {
        bundle.bundle.bundledItems.forEach(bundleItem => {
          if (!selectedProducts.find(p => p.id === bundleItem.catalogItem)) {
            const product = products.find(p => p.id === bundleItem.catalogItem);
            if (!product) throw new Error('Invalid product lookup');

            selectedProducts.push(product);
          }

          bundledItems.push({
            ...bundleItem,
            count: bundleItem.count * bundle.quantity
          });
        });

        if (!selectedProducts.find(p => p.id === bundle.bundle.mainDevice)) {
          const product = products.find(p => p.id === bundle.bundle.mainDevice);
          if (!product) throw new Error('Invalid product lookup');

          selectedProducts.push(product);
        }

        bundledItems.push({
          catalogItem: bundle.bundle.mainDevice,
          count: 1 * bundle.quantity
        });
      });

      const productsGroupedUpByCategory = groupProductsByCategories(selectedProducts, categories);

      const selectedModelObjects = models.filter(model => selectedModels.financeModelIds.includes(model.id));

      const groupdedBundledItems = bundledItems.reduce<Record<string, BundledItem>>((acc, item) => {
        // If the catalogItem is already in the accumulator, add to its count
        if (acc[item.catalogItem]) {
          acc[item.catalogItem].count += item.count;
        } else {
          // If it does not exist, add the item as is
          acc[item.catalogItem] = { ...item };
        }
        return acc;
      }, {});

      return {
        customerId,
        mspId,
        deliveryOption: PurchaseOrderDeliveryOption.Daas,
        name,
        bundledItems: Object.values(groupdedBundledItems),
        state: PoStatus.Draft
      };
    },
    [categories, products, selectedModels, models]
  );
};
